import { IDentalData } from '@dataTypes/chartingFeature.types'
import {
  chartingTokensToAbbrevParts,
  parseChartingSurfaceTokens,
} from '@utils/charting-surface-display.utils'

// Reverse mapping of LABEL_TO_CONDITION for condition code to label
const CONDITION_TO_LABEL: Record<string, string> = {
  caries: 'Caries',
  inlay: 'Inlay',
  onlay: 'Onlay',
  abrasion: 'Abrasion/abfraction',
  erosion: 'Erosion',
  implant: 'Implant',
  veneer: 'Veneer',
  filling: 'Filling',
  crown: 'Crown',
  furcation: 'Furcation',
  recession: 'Recession',
  attrition: 'Attrition',
  bridge: 'Bridges',
  sealants: 'Sealants',
  missing: 'Missing',
  crack: 'Crack',
  fracture: 'Fracture',
  root_canal: 'Root canal',
  apicoectomy: 'Apicoectomy',
  periapical: 'Periapical',
  periradicular: 'Periradicular',
  pulpotomy: 'Pulpotomy',
  pulpectomy: 'Pulpectomy',
  sinus_tract: 'Sinus tract',
  impacted: 'Impacted',
  drifted: 'Drifted',
  supernumerary: 'Supernumerary',
  retainer: 'Retained primary',
}

// Material label formatting
const formatMaterial = (material: string | null): string => {
  if (!material) return ''
  return material
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Type label formatting - handles special codes and regular types
const formatType = (type: string | null): string => {
  if (!type) return ''

  // Special codes that should stay uppercase (D1, D2, D3, E1, E2, C1, C2, C3)
  const upperCaseCodes = /^(D[1-3]|E[1-2]|C[1-3])$/i
  if (upperCaseCodes.test(type)) {
    return type.toUpperCase()
  }

  // Handle class1, class2, class3, class4 - format as "Class 1", "Class 2", etc.
  const classMatch = type.match(/^class([1-4])$/i)
  if (classMatch) {
    return `Class ${classMatch[1]}`
  }

  // Format other types (moderate, slight, severe, left_support, crown_vertical, etc.)
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Format condition label
const formatConditionLabel = (condition: string): string => {
  return (
    CONDITION_TO_LABEL[condition.toLowerCase()] ||
    condition.charAt(0).toUpperCase() + condition.slice(1).toLowerCase()
  )
}

const getConditionGroupKey = (entry: IDentalData): string =>
  [
    entry.condition,
    entry.type ?? '',
    entry.material ?? '',
    entry.recurrent ? '1' : '0',
  ].join('|')

const formatConditionBaseLabel = (entry: IDentalData): string => {
  let conditionStr = formatConditionLabel(entry.condition)
  if (entry.type) {
    conditionStr += ` (${formatType(entry.type)})`
  }
  if (entry.material) {
    conditionStr += ` (${formatMaterial(entry.material)})`
  }
  return conditionStr
}

const formatGroupedConditionLine = (
  entry: IDentalData,
  surfaceTokens: string[],
  toothNumber: number,
): string => {
  const parts: string[] = [formatConditionBaseLabel(entry)]
  const surfaces = chartingTokensToAbbrevParts(surfaceTokens, toothNumber).join(
    ', ',
  )
  if (surfaces) {
    parts.push(`- ${surfaces}`)
  }
  if (entry.recurrent) {
    parts.push('[Recurrent]')
  }
  return parts.join(' ')
}

const formatToothConditions = (
  entries: IDentalData[],
  toothNumber: number,
): string[] => {
  const groups = new Map<
    string,
    { entry: IDentalData; surfaceTokens: string[] }
  >()
  for (const entry of entries) {
    const key = getConditionGroupKey(entry)
    const existing = groups.get(key)
    if (!existing) {
      groups.set(key, {
        entry,
        surfaceTokens: parseChartingSurfaceTokens(entry.surface),
      })
      continue
    }
    existing.surfaceTokens.push(...parseChartingSurfaceTokens(entry.surface))
  }
  return Array.from(groups.values()).map(({ entry, surfaceTokens }) =>
    formatGroupedConditionLine(entry, surfaceTokens, toothNumber),
  )
}

export interface FormattedToothEntry {
  toothNumber: number
  conditions: string[]
}

export interface DentalDataSummary {
  totalTeeth: number
  totalEntries: number
  totalConditions: number
  summary: FormattedToothEntry[]
}

/**
 * Formats dental_data array into a readable summary
 * @param dental_data Array of dental data entries
 * @returns Formatted summary object with total counts and grouped entries
 */
export const formatDentalDataSummary = (
  dental_data: IDentalData[] | undefined | null,
): DentalDataSummary => {
  if (!dental_data || dental_data.length === 0) {
    return {
      totalTeeth: 0,
      totalEntries: 0,
      totalConditions: 0,
      summary: [],
    }
  }

  // Group by tooth number
  const groupedByTooth: Record<number, IDentalData[]> = {}

  dental_data.forEach((entry) => {
    const toothNum =
      typeof entry.tooth_number === 'string'
        ? parseInt(entry.tooth_number, 10)
        : entry.tooth_number

    if (!isNaN(toothNum)) {
      if (!groupedByTooth[toothNum]) {
        groupedByTooth[toothNum] = []
      }
      groupedByTooth[toothNum].push(entry)
    }
  })

  // Format each tooth's conditions
  const summary: FormattedToothEntry[] = Object.keys(groupedByTooth)
    .map((toothNumStr) => parseInt(toothNumStr, 10))
    .sort((a, b) => a - b)
    .map((toothNum) => {
      const entries = groupedByTooth[toothNum]
      return {
        toothNumber: toothNum,
        conditions: formatToothConditions(entries, toothNum),
      }
    })

  const totalConditions = summary.reduce(
    (count, tooth) => count + tooth.conditions.length,
    0,
  )

  return {
    totalTeeth: summary.length,
    totalEntries: dental_data.length,
    totalConditions,
    summary,
  }
}
